import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import { CloseButton, CancelButton, StandardButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';
import { driveApi, type DriveRow } from '@/features/drive/lib/drive-api';
import { DriveItemIcon } from '@/features/drive/components/DriveItemParts';
import type { DriveFolderNode, DriveItem } from '@/features/drive/types';
import { createDriveFileRef } from '@/lib/drive-file-ref';
import '@/design-system/forms.css';
import '@/design-system/typography.css';

/**
 * The ONE "Browse Drive" picker of the app: the folder tree of a project's Drive (folders expand in place, files are ticked) that
 * hands back the picked files as `File` stand-ins (lib/drive-file-ref.ts). It is opened by the generic upload popup
 * (GenericFileUploadModal), so every attachments section gets it at once; nothing here knows about a module.
 *
 * Reads go through the Drive API (company / project / permissions are the server's): `/api/drive/folders` once, then
 * `/api/drive/items` for each folder the first time it is expanded. It only ever shows the project it is given.
 */
interface BrowseDriveModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The project whose Drive is shown. Empty / 'all' = none selected: the popup asks for one instead of showing anything. */
  projectId?: string | null;
  /** The picked files (as Drive stand-ins). The caller decides what happens next; this popup closes itself after. */
  onConfirm: (files: File[]) => void;
}

type FolderState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; items: DriveRow[] };

const formatSize = (bytes?: number | null) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

const ROOT = 'root';

export function BrowseDriveModal({ isOpen, onClose, projectId, onConfirm }: BrowseDriveModalProps) {
  const project = projectId && projectId !== 'all' ? projectId : null;
  const [folders, setFolders] = useState<DriveFolderNode[] | null>(null);
  const [foldersError, setFoldersError] = useState<string | null>(null);
  const [contents, setContents] = useState<Record<string, FolderState>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set([ROOT]));
  const [selected, setSelected] = useState<Map<string, DriveRow>>(new Map());

  const loadFolder = useCallback(async (folderId: string, projectForLoad: string) => {
    setContents((prev) => ({ ...prev, [folderId]: { status: 'loading' } }));
    try {
      const { items } = await driveApi.listFolder(projectForLoad, folderId === ROOT ? null : folderId);
      setContents((prev) => ({ ...prev, [folderId]: { status: 'ready', items } }));
    } catch (e) {
      setContents((prev) => ({ ...prev, [folderId]: { status: 'error', message: e instanceof Error ? e.message : 'The folder could not be loaded' } }));
    }
  }, []);

  // A fresh browse each time it opens (or the project changes): nothing of another project is ever kept around.
  useEffect(() => {
    if (!isOpen) return undefined;
    setFolders(null);
    setFoldersError(null);
    setContents({});
    setExpanded(new Set([ROOT]));
    setSelected(new Map());
    if (!project) return undefined;
    let cancelled = false;
    driveApi.listFolders(project)
      .then((list) => { if (!cancelled) setFolders(list); })
      .catch((e) => { if (!cancelled) setFoldersError(e instanceof Error ? e.message : 'The Drive could not be loaded'); });
    void loadFolder(ROOT, project);
    return () => { cancelled = true; };
  }, [isOpen, project, loadFolder]);

  const childFolders = useMemo(() => {
    const map = new Map<string, DriveFolderNode[]>();
    for (const folder of folders || []) {
      const key = folder.parent_id || ROOT;
      map.set(key, [...(map.get(key) || []), folder]);
    }
    return map;
  }, [folders]);

  if (!isOpen) return null;

  const toggleFolder = (folderId: string) => {
    const next = new Set(expanded);
    if (next.has(folderId)) next.delete(folderId);
    else {
      next.add(folderId);
      if (project && !contents[folderId]) void loadFolder(folderId, project);
    }
    setExpanded(next);
  };

  const toggleFile = (row: DriveRow) => {
    const next = new Map(selected);
    if (next.has(row.id)) next.delete(row.id);
    else next.set(row.id, row);
    setSelected(next);
  };

  const confirm = () => {
    onConfirm(Array.from(selected.values()).map((row) => createDriveFileRef({ id: row.id, name: row.name, size: row.size, mime_type: row.mime_type })));
    onClose();
  };

  const renderFolder = (folderId: string, depth: number): React.ReactNode => {
    const state = contents[folderId];
    const files = state?.status === 'ready' ? state.items.filter((item) => item.type === 'file') : [];
    const subfolders = childFolders.get(folderId) || [];
    const indent = { paddingLeft: `${depth * 1.25}rem` };
    return (
      <div role="group">
        {subfolders.map((folder) => {
          const isOpenFolder = expanded.has(folder.id);
          return (
            <div key={folder.id}>
              <button type="button" className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50" style={indent} onClick={() => toggleFolder(folder.id)} aria-expanded={isOpenFolder}>
                {isOpenFolder ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                <Folder className="h-4 w-4 text-gray-400" />
                <span className="truncate">{folder.name}</span>
              </button>
              {isOpenFolder && renderFolder(folder.id, depth + 1)}
            </div>
          );
        })}
        {state?.status === 'loading' && <p className="px-2 py-1.5 text-sm text-gray-500" style={indent}>Loading...</p>}
        {state?.status === 'error' && (
          <p className="px-2 py-1.5 text-sm text-destructive" style={indent}>
            {state.message}{' '}
            <button type="button" className="underline" onClick={() => project && loadFolder(folderId, project)}>Retry</button>
          </p>
        )}
        {files.map((row) => (
          <label key={row.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50" style={indent}>
            <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleFile(row)} aria-label={row.name} />
            <DriveItemIcon item={row as unknown as DriveItem} />
            <span className="min-w-0 flex-1 truncate" title={row.name}>{row.name}</span>
            <span className="flex-shrink-0 text-xs text-gray-500">{formatSize(row.size)}</span>
          </label>
        ))}
        {state?.status === 'ready' && files.length === 0 && subfolders.length === 0 && (
          <p className="px-2 py-1.5 text-sm text-gray-500" style={indent}>This folder is empty</p>
        )}
      </div>
    );
  };

  const selectedRows = Array.from(selected.values());

  const modal = (
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="generic-form-container w-full max-w-lg" style={{ maxHeight: 'var(--form-max-height-compact)' }} onClick={(e) => e.stopPropagation()}>
        <div className="generic-form-header generic-form-header-compact" style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">Browse Drive</h2>
          <CloseButton onClick={onClose} />
        </div>

        <div className="generic-form-body generic-form-body-compact">
          {!project ? (
            <p className="py-8 text-center text-sm text-gray-600">Select a project to browse its Drive.</p>
          ) : foldersError ? (
            <p className="py-8 text-center text-sm text-destructive">{foldersError}</p>
          ) : (
            <>
              <div className="rounded-md border border-border p-1" style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                <button type="button" className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm font-medium hover:bg-gray-50" onClick={() => toggleFolder(ROOT)} aria-expanded={expanded.has(ROOT)}>
                  {expanded.has(ROOT) ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                  <Folder className="h-4 w-4 text-gray-400" />
                  Drive
                </button>
                {expanded.has(ROOT) && renderFolder(ROOT, 1)}
              </div>
              {/* a fixed-height area: ticking a file must not move the list under the pointer */}
              <div className="mt-3 space-y-1" style={{ height: '6rem' }} aria-label="Selected files">
                {selectedRows.length === 0 ? (
                  <p className="text-sm text-gray-500">No files selected</p>
                ) : (
                  <>
                    <p className="text-sm font-medium">{selectedRows.length} selected</p>
                    <ul className="max-h-[4.5rem] overflow-y-auto text-sm text-gray-700">
                      {selectedRows.map((row) => (
                        <li key={row.id} className="flex items-center justify-between gap-2">
                          <span className="truncate" title={row.name}>{row.name}</span>
                          <button type="button" className="flex-shrink-0 text-xs underline" onClick={() => toggleFile(row)}>Remove</button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="generic-form-footer generic-form-footer-compact" style={{ backgroundColor: colors.formHeaderFooter }}>
          <CancelButton onClick={onClose} />
          <StandardButton customVariant="blue" type="button" disabled={selectedRows.length === 0} onClick={confirm}>
            {selectedRows.length > 0 ? `Attach ${selectedRows.length} file${selectedRows.length > 1 ? 's' : ''}` : 'Attach'}
          </StandardButton>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
